// client/src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe ser usado dentro de AuthProvider");
  }
  return context;
};

// Configurar axios interceptors
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:3001/api",
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("accessToken"));

  // Configurar interceptor para requests
  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("accessToken");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Configurar interceptor para responses
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;

      if (error.response?.status === 401 && !original._retry) {
        original._retry = true;

        try {
          const refreshToken = localStorage.getItem("refreshToken");
          if (refreshToken) {
            const response = await axios.post(
              `${process.env.REACT_APP_API_URL}/auth/refresh-token`,
              { refreshToken }
            );

            const { accessToken, refreshToken: newRefreshToken } =
              response.data.tokens;

            localStorage.setItem("accessToken", accessToken);
            localStorage.setItem("refreshToken", newRefreshToken);
            setToken(accessToken);

            // Reintentar request original
            original.headers.Authorization = `Bearer ${accessToken}`;
            return api(original);
          }
        } catch (refreshError) {
          console.error("Error renovando token:", refreshError);
          logout();
        }
      }

      return Promise.reject(error);
    }
  );

  const login = async (correo, contraseña) => {
    try {
      setLoading(true);
      const response = await api.post("/auth/login", { correo, contraseña });

      const { user: userData, tokens } = response.data;

      // Guardar tokens
      localStorage.setItem("accessToken", tokens.accessToken);
      localStorage.setItem("refreshToken", tokens.refreshToken);
      setToken(tokens.accessToken);

      // Obtener perfil completo. loadUserProfile() devuelve el usuario CON sus
      // permisos, así que lo devolvemos aquí también: quien llame a login()
      // necesita decidir a dónde mandar al usuario, y en ese punto
      // hasPermission() todavía cierra sobre el usuario ANTERIOR al login
      // (user = null), o sea que siempre daría false.
      const perfil = await loadUserProfile();

      return { success: true, message: response.data.message, user: perfil };
    } catch (error) {
      console.error("Error en login:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Error en el login",
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken) {
        await api.post("/auth/logout", { refreshToken });
      }
    } catch (error) {
      console.error("Error en logout:", error);
    } finally {
      // Limpiar estado local
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      setToken(null);
      setUser(null);
    }
  };

  const loadUserProfile = async () => {
    try {
      const response = await api.get("/auth/profile");
      setUser(response.data.user);
      return response.data.user;
    } catch (error) {
      console.error("Error cargando perfil:", error);
      logout();
      return null;
    }
  };

  const hasPermission = (permission) => {
    return user?.permisos?.includes(permission) || false;
  };

  const hasAnyPermission = (permissions) => {
    return permissions.some((permission) => hasPermission(permission));
  };

  const hasRole = (role) => {
    return user?.rol === role;
  };

  const checkAuth = async () => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      setToken(token);
      await loadUserProfile();
    }
    setLoading(false);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    hasPermission,
    hasAnyPermission,
    hasRole,
    api, // Exportar instancia configurada de axios
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { api };
